import { ImageFileId } from '../../component/image/image-file-id'
import { PageContainer } from 'src/component/page-container/page-container'
import { Button } from '../../component/ui/button'
import { Input } from '../../component/ui/input/input'
import { PageTitle } from '../../component/ui/page-title'
import React, { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAdminComments, useDeleteAdminComment, useUpdateAdminComment } from 'src/hook/use-community'
import { CommunityComment, CommunityEntityType } from 'src/types/community'
import { stringUtil } from 'src/util/string-util'
import { timeUtil } from 'src/util/time-util'
import { toasterUtil } from 'src/util/toaster-util'

const ENTITY_TYPES: Array<'all' | CommunityEntityType> = ['all', 'workout', 'series', 'post']

const buildEntityLink = (comment: CommunityComment): string | null => {
  switch (comment.entityType) {
    case 'workout':
      return `/workouts/${comment.entityId}`
    case 'series':
      return `/programmes/${comment.entityId}`
    case 'post': {
      const groupId = comment.entityContext?.groupId
      return groupId ? `/groups/${groupId}` : null
    }
    default:
      return null
  }
}

export const AdminCommentsScreen = (): React.ReactElement => {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [entityTypeFilter, setEntityTypeFilter] = useState<'all' | CommunityEntityType>('all')
  const [offset, setOffset] = useState(0)
  const [editing, setEditing] = useState<{ comment: CommunityComment; text: string } | null>(null)
  const limit = 20

  const { data, isLoading, error, refetch } = useAdminComments({
    limit,
    offset,
    search: search || undefined,
    entityType: entityTypeFilter === 'all' ? undefined : entityTypeFilter,
    includeReplies: true,
  })
  const deleteMutation = useDeleteAdminComment()
  const updateMutation = useUpdateAdminComment()

  useEffect(() => {
    if (error && (error as any)?.response?.status === 403) {
      navigate('/', { replace: true })
    }
  }, [error, navigate])

  const handleDelete = (comment: CommunityComment): void => {
    const preview = comment.text.length > 60 ? `${comment.text.slice(0, 60)}…` : comment.text
    if (window.confirm(`Delete this comment?\n\n"${preview}"`)) {
      deleteMutation.mutate(comment.id, {
        onSuccess: () => {
          toasterUtil.success('Comment deleted')
          refetch()
        },
        onError: () => toasterUtil.error('Failed to delete comment'),
      })
    }
  }

  const handleSaveEdit = (): void => {
    if (!editing) return
    const trimmed = editing.text.trim()
    if (!trimmed) {
      toasterUtil.error('Comment cannot be empty')
      return
    }
    updateMutation.mutate(
      { commentId: editing.comment.id, payload: { text: trimmed } },
      {
        onSuccess: () => {
          toasterUtil.success('Comment updated')
          setEditing(null)
          refetch()
        },
        onError: () => toasterUtil.error('Failed to update comment'),
      },
    )
  }

  const comments = data?.data ?? []
  const total = data?.total ?? 0
  const hasMore = offset + limit < total
  const hasPrevious = offset > 0

  return (
    <PageContainer>
      <div className="py-6 md:py-[82px]">
        <div className="mb-6 flex items-center justify-between">
          <PageTitle>Admin - Comments</PageTitle>
        </div>

        {/* Tab switcher placeholder — only Comments for now */}
        <div className="border-tertiary-grey mb-6 border-b">
          <button className="border-ivory text-ivory border-b-2 px-4 py-2 font-semibold">Comments</button>
        </div>

        <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-center">
          <div className="flex-1">
            <Input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
                setOffset(0)
              }}
              placeholder="Search comment text..."
            />
          </div>
          <select
            value={entityTypeFilter}
            onChange={(e) => {
              setEntityTypeFilter(e.target.value as 'all' | CommunityEntityType)
              setOffset(0)
            }}
            className="bg-secondary-gray text-light-gray border-tertiary-grey h-12 rounded-md border px-3 py-2 capitalize"
          >
            {ENTITY_TYPES.map((t) => (
              <option key={t} value={t}>
                {t === 'all' ? 'All entities' : t}
              </option>
            ))}
          </select>
        </div>

        {isLoading ? (
          <div className="bg-primary-gray rounded-lg p-6">
            <div className="animate-pulse space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-secondary-gray h-16 rounded"></div>
              ))}
            </div>
          </div>
        ) : comments.length === 0 ? (
          <div className="bg-primary-gray rounded-lg p-6 text-center">
            <p className="text-mushroom">No comments found</p>
          </div>
        ) : (
          <div className="bg-primary-gray overflow-hidden rounded-lg">
            <table className="w-full">
              <thead className="bg-secondary-gray">
                <tr>
                  <th className="text-mushroom px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Author</th>
                  <th className="text-mushroom px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Comment</th>
                  <th className="text-mushroom px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Entity</th>
                  <th className="text-mushroom px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Posted</th>
                  <th className="text-mushroom px-6 py-3 text-right text-xs font-medium uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-tertiary-grey divide-y">
                {comments.map((c) => (
                  <tr key={c.id} className="hover:bg-secondary-gray">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="bg-secondary-gray text-light-gray flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-xs font-semibold">
                          {c.hustler?.profilePhotoFileId ? (
                            <ImageFileId fileId={c.hustler.profilePhotoFileId} className="h-8 w-8 rounded-full object-cover" />
                          ) : (
                            stringUtil.initials(stringUtil.getDisplayName(c.hustler) ?? '')
                          )}
                        </div>
                        <div className="min-w-0">
                          <div className="text-ivory truncate font-semibold">{stringUtil.getDisplayName(c.hustler ?? null)}</div>
                          {c.hustler?.username && <div className="text-mushroom truncate text-xs">@{c.hustler.username}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="max-w-md px-6 py-4">
                      {editing?.comment.id === c.id ? (
                        <textarea
                          value={editing.text}
                          onChange={(e) => setEditing({ ...editing, text: e.target.value })}
                          className="bg-secondary-gray text-light-gray border-tertiary-grey w-full rounded border p-2 text-sm"
                          rows={3}
                          maxLength={500}
                        />
                      ) : (
                        <p className="text-light-gray whitespace-pre-wrap break-words text-sm">
                          {c.text}
                          {c.parentCommentId && (
                            <span className="bg-tertiary-grey text-light-gray ml-2 rounded px-1.5 py-0.5 text-[10px] uppercase">
                              Reply
                            </span>
                          )}
                        </p>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {(() => {
                        const href = buildEntityLink(c)
                        const inner = (
                          <div className="text-sm">
                            <div className="text-light-gray capitalize">{c.entityType}</div>
                            <div className="text-mushroom break-all text-xs">{c.entityId}</div>
                          </div>
                        )
                        return href ? (
                          <Link to={href} className="hover:text-bronze block">
                            {inner}
                          </Link>
                        ) : (
                          inner
                        )
                      })()}
                    </td>
                    <td className="text-mushroom px-6 py-4 text-sm">{timeUtil.formatRelativeTime(c.createdAt)}</td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end gap-2">
                        {editing?.comment.id === c.id ? (
                          <>
                            <Button size="sm" variant="default" onClick={handleSaveEdit} isLoading={updateMutation.isLoading}>
                              Save
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setEditing(null)}>
                              Cancel
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button
                              size="sm"
                              variant="default"
                              className="w-24 text-white"
                              onClick={() => setEditing({ comment: c, text: c.text })}
                            >
                              Edit
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              className="w-24"
                              onClick={() => handleDelete(c)}
                              isLoading={deleteMutation.isLoading && deleteMutation.variables === c.id}
                            >
                              Delete
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {(hasMore || hasPrevious) && (
          <div className="mt-6 flex items-center justify-between">
            <Button variant="secondary" onClick={() => setOffset(offset - limit)} disabled={!hasPrevious}>
              Previous
            </Button>
            <span className="text-mushroom">
              Showing {total === 0 ? 0 : offset + 1}–{Math.min(offset + limit, total)} of {total}
            </span>
            <Button variant="secondary" onClick={() => setOffset(offset + limit)} disabled={!hasMore}>
              Next
            </Button>
          </div>
        )}
      </div>
    </PageContainer>
  )
}
